import { useMemo } from 'react';
import type { ReactElement } from 'react';
import type { HeatmapDay } from '@/lib/types';

type Props = {
  startDate: string;
  endDate: string;
  activity: HeatmapDay[];
};

type DayCell = {
  date: string;
  sessionCount: number;
  totalMinutes: number;
};

// Intensity uses the design tokens documented in BACKLOG:
// 0 sessions  → --bg-soft (empty, neutral)
// 1 session   → --brand-wash (lightest brand tint)
// 2 sessions  → #b4bec9 (one-off cool mid-tone, bridges wash to brand)
// 3+ sessions → --brand (full brand colour)
const INTENSITY_COLOR_FOR_ZERO = 'var(--bg-soft)';
const INTENSITY_COLOR_FOR_ONE = 'var(--brand-wash)';
const INTENSITY_COLOR_FOR_TWO = '#b4bec9';
const INTENSITY_COLOR_FOR_THREE_OR_MORE = 'var(--brand)';

function intensityColor(sessionCount: number): string {
  if (sessionCount === 0) return INTENSITY_COLOR_FOR_ZERO;
  if (sessionCount === 1) return INTENSITY_COLOR_FOR_ONE;
  if (sessionCount === 2) return INTENSITY_COLOR_FOR_TWO;
  return INTENSITY_COLOR_FOR_THREE_OR_MORE;
}

// ISO weekday order — Monday-first to match en-GB conventions used elsewhere
// in the app. Visible character row is M T W T F S S; aria-label exposes the
// full weekday name to assistive tech.
const WEEKDAY_LABELS: readonly { short: string; full: string }[] = [
  { short: 'M', full: 'Monday' },
  { short: 'T', full: 'Tuesday' },
  { short: 'W', full: 'Wednesday' },
  { short: 'T', full: 'Thursday' },
  { short: 'F', full: 'Friday' },
  { short: 'S', full: 'Saturday' },
  { short: 'S', full: 'Sunday' },
] as const;

function isoWeekdayIndex(date: Date): number {
  // JS Date.getDay(): Sunday = 0, …, Saturday = 6.
  // ISO ordering puts Monday at 0 and Sunday at 6.
  return (date.getDay() + 6) % 7;
}

function buildGrid(startDate: string, endDate: string, activity: HeatmapDay[]): DayCell[][] {
  const activityMap = new Map<string, HeatmapDay>();
  for (const day of activity) {
    activityMap.set(day.date, day);
  }

  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');

  const days: DayCell[] = [];

  const startDayOfWeek = isoWeekdayIndex(start);
  for (let i = 0; i < startDayOfWeek; i++) {
    days.push({ date: '', sessionCount: -1, totalMinutes: 0 });
  }

  const current = new Date(start);
  while (current <= end) {
    const dateStr = current.toISOString().slice(0, 10);
    const entry = activityMap.get(dateStr);
    days.push({
      date: dateStr,
      sessionCount: entry?.sessionCount ?? 0,
      totalMinutes: entry?.totalMinutes ?? 0,
    });
    current.setDate(current.getDate() + 1);
  }

  const weeks: DayCell[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return weeks;
}

export function PracticeHeatmap({ startDate, endDate, activity }: Props): ReactElement {
  const weeks = useMemo(() => buildGrid(startDate, endDate, activity), [startDate, endDate, activity]);

  return (
    <div className="flex gap-1 overflow-x-auto">
      <div className="flex flex-col gap-0.5 pr-1 text-[9px] text-[var(--ink-3)] font-mono select-none">
        {WEEKDAY_LABELS.map((wd) => (
          <span
            key={wd.full}
            aria-label={wd.full}
            className="w-3 h-3 leading-3 text-center"
          >
            {wd.short}
          </span>
        ))}
      </div>
      <div role="grid" className="flex gap-0.5">
        {weeks.map((week, weekIdx) => (
          <div key={weekIdx} role="row" className="flex flex-col gap-0.5">
            {week.map((day, dayIdx) => (
              <div
                key={dayIdx}
                role="gridcell"
                title={day.date ? `${day.date}: ${day.sessionCount} session(s), ${day.totalMinutes} min` : ''}
                className="w-3 h-3 rounded-sm"
                style={{
                  background: day.sessionCount < 0 ? 'transparent' : intensityColor(day.sessionCount),
                }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
